import { useState, useEffect, useCallback } from "react";

const PROXY    = "http://localhost:4000/api";
const getToken = () => localStorage.getItem("sellsync_token");

const api = async (path, opts = {}) => {
  const res = await fetch(
    (process.env.REACT_APP_API_URL || PROXY) + path,
    {
      ...opts,
      headers: {
        "Content-Type": "application/json",
        ...(getToken() ? { Authorization: "Bearer " + getToken() } : {}),
        ...opts.headers,
      },
    }
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
};

const INR = (n) => "₹" + Number(n / 100).toLocaleString("en-IN");

const PLAN_FEATURES = {
  starter: [
    "2 platforms (Shopify + 1 more)",
    "Up to 500 SKUs",
    "Up to 500 orders/month",
    "Inventory sync",
    "GST invoicing",
    "Basic analytics",
    "Email support",
  ],
  growth: [
    "All 4 platforms",
    "Up to 5,000 SKUs",
    "Up to 5,000 orders/month",
    "SKU mapping manager",
    "Logistics integration",
    "Returns management",
    "GST reports + state-wise",
    "Purchase orders",
    "Priority support",
  ],
  enterprise: [
    "All 4 platforms",
    "Unlimited SKUs",
    "Unlimited orders",
    "Everything in Growth",
    "Multi-warehouse",
    "Advanced analytics",
    "Dedicated account manager",
    "Custom integrations",
    "Phone support",
  ],
};

const S = {
  card:    { background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb", marginBottom: 14 },
  cardHdr: { padding: "11px 16px", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "space-between", fontWeight: 600, fontSize: 13 },
  btn:     (c = "#F47920") => ({ padding: "10px 20px", borderRadius: 8, fontSize: 13, cursor: "pointer", background: c, color: "#fff", border: "none", fontFamily: "inherit", fontWeight: 600, width: "100%", marginTop: 16 }),
  btnOut:  { padding: "8px 16px", borderRadius: 8, fontSize: 12, cursor: "pointer", background: "#fff", color: "#374151", border: "1px solid #d1d5db", fontFamily: "inherit" },
};

const Badge = ({ label, color, bg }) => (
  <span style={{ background: bg, color, padding: "2px 9px", borderRadius: 20, fontSize: 11, fontWeight: 600 }}>{label}</span>
);

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload  = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default function PageBilling({ toast, tenant, onPlanUpgrade }) {
  const [plans,   setPlans]   = useState([]);
  const [status,  setStatus]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying,  setPaying]  = useState("");

  const loadData = useCallback(async () => {
    try {
      const [planData, statusData] = await Promise.all([
        api("/billing/plans"),
        api("/billing/status"),
      ]);
      setPlans(planData.plans || []);
      setStatus(statusData);
    } catch(e) { toast(e.message, "error"); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleUpgrade = async (planId) => {
    setPaying(planId);
    try {
      const loaded = await loadRazorpay();
      if (!loaded) { toast("Razorpay failed to load. Check your connection.", "error"); setPaying(""); return; }

      // create order
      const order = await api("/billing/create-order", {
        method: "POST",
        body: JSON.stringify({ planId }),
      });

      // open Razorpay checkout
      const options = {
        key:         order.keyId,
        amount:      order.amount,
        currency:    order.currency,
        name:        "3June",
        description: order.planName + " Plan — 1 month",
        image:       "https://3june.com/logo192.png",
        order_id:    order.orderId,
        prefill: {
          name:  order.name,
          email: order.email,
        },
        theme: { color: "#F47920" },
        handler: async function(response) {
          try {
            const verify = await api("/billing/verify", {
              method: "POST",
              body: JSON.stringify({
                razorpay_order_id:   response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature:  response.razorpay_signature,
                planId,
              }),
            });
            toast("🎉 Payment successful! Plan upgraded to " + order.planName, "success");
            loadData();
            if (onPlanUpgrade) onPlanUpgrade(verify.plan, verify.limits);
          } catch(e) { toast(e.message, "error"); }
        },
        modal: {
          ondismiss: () => { setPaying(""); },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", function(response) {
        toast("Payment failed: " + response.error.description, "error");
        setPaying("");
      });
      rzp.open();
    } catch(e) {
      toast(e.message, "error");
      setPaying("");
    }
  };

  const planColor = { trial: "#6b7280", starter: "#2563eb", growth: "#F47920", enterprise: "#7c3aed" };
  const [referral, setReferral] = useState(null);
  const [copying,  setCopying]  = useState(false);

  useEffect(() => {
    api("/auth/referral").then(d => { if (d.ok) setReferral(d); }).catch(()=>{});
  }, []);

  const copyLink = () => {
    if (!referral?.referralLink) return;
    navigator.clipboard.writeText(referral.referralLink);
    setCopying(true);
    setTimeout(() => setCopying(false), 2000);
  };

  const applyCredit = async () => {
    try {
      const d = await api("/auth/referral/apply", { method:"POST" });
      if (d.ok) { toast(d.message, "success"); setReferral(r => ({ ...r, creditsRemaining:d.creditsRemaining })); }
    } catch(e) { toast(e.message, "error"); }
  };

  const currentPlan = tenant?.plan || "trial";
  const trialDays = status?.trialDaysLeft || 0;
  const planDays  = status?.planDaysLeft  || 0;

  return (
    <>
    <div>
      {/* current plan banner */}
      <div style={{ ...S.card, padding: 20, borderLeft: `4px solid ${planColor[currentPlan]}`, marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{tenant?.storeName}</div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>{tenant?.email}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <Badge label={currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)}
              color={planColor[currentPlan]} bg={planColor[currentPlan] + "18"}/>
            {currentPlan === "trial" && (
              <div style={{ fontSize: 11, color: trialDays <= 3 ? "#dc2626" : "#d97706", marginTop: 6 }}>
                ⏳ {trialDays} days left in free trial
              </div>
            )}
            {currentPlan !== "trial" && planDays > 0 && (
              <div style={{ fontSize: 11, color: "#6b7280", marginTop: 6 }}>
                Renews in {planDays} days
              </div>
            )}
          </div>
        </div>

        {currentPlan === "trial" && trialDays <= 3 && (
          <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13, color: "#b91c1c" }}>
            ⚠️ Your trial expires in {trialDays} day{trialDays !== 1 ? "s" : ""}. Upgrade now to keep access to all features.
          </div>
        )}

        {/* current limits */}
        {status?.limits && (() => {
          const lim = status.limits;
          // Accept both field naming conventions (platforms/maxPlatforms etc.) and guard undefined
          const fmt = (v) => {
            if (v == null) return "—";
            if (v === 99999 || v >= 1000000) return "Unlimited";
            return Number(v).toLocaleString();
          };
          const platforms = lim.platforms ?? lim.maxPlatforms;
          const skus      = lim.skus      ?? lim.maxSkus;
          const orders    = lim.orders    ?? lim.maxOrders;
          return (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginTop: 14 }}>
            {[
              ["Platforms", platforms == null ? "—" : (platforms === 99999 || platforms >= 99 ? "Unlimited" : platforms)],
              ["SKUs",      fmt(skus)],
              ["Orders/mo", fmt(orders)],
            ].map(([l, v]) => (
              <div key={l} style={{ background: "#f9fafb", borderRadius: 8, padding: "10px 14px", textAlign: "center" }}>
                <div style={{ fontSize: 10, color: "#9ca3af", marginBottom: 4 }}>{l}</div>
                <div style={{ fontWeight: 700, fontSize: 16, color: planColor[currentPlan] }}>{v}</div>
              </div>
            ))}
          </div>
          );
        })()}
      </div>

      {/* pricing plans */}
      <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Choose a Plan</div>
      <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 16 }}>
        All plans include 14-day free trial · Cancel anytime · GST invoice provided
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16 }}>
        {loading ? (
          <div style={{ gridColumn: "1/-1", textAlign: "center", padding: 40, color: "#9ca3af" }}>Loading plans…</div>
        ) : plans.map((plan) => {
          const isCurrent  = currentPlan === plan.id;
          const isPopular  = plan.id === "growth";
          const features   = PLAN_FEATURES[plan.id] || [];
          const color      = planColor[plan.id];

          return (
            <div key={plan.id} style={{
              background: "#fff", borderRadius: 12,
              border: isPopular ? `2px solid ${color}` : "1px solid #e5e7eb",
              padding: 24, position: "relative",
              boxShadow: isPopular ? "0 4px 20px rgba(244,121,32,0.15)" : "none",
            }}>
              {isPopular && (
                <div style={{ position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)", background: color, color: "#fff", fontSize: 11, fontWeight: 700, padding: "3px 14px", borderRadius: 20, whiteSpace: "nowrap" }}>
                  Most Popular
                </div>
              )}

              <div style={{ fontWeight: 700, fontSize: 16, color, marginBottom: 6 }}>{plan.name}</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 4, marginBottom: 4 }}>
                <span style={{ fontSize: 32, fontWeight: 800, color: "#111" }}>{INR(plan.amount)}</span>
                <span style={{ fontSize: 13, color: "#9ca3af" }}>/month</span>
              </div>
              <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 16 }}>Billed monthly · GST extra</div>

              <div style={{ marginBottom: 16 }}>
                {features.map((f) => (
                  <div key={f} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, fontSize: 13 }}>
                    <span style={{ color: "#22c55e", fontWeight: 700, flexShrink: 0 }}>✓</span>
                    <span style={{ color: "#374151" }}>{f}</span>
                  </div>
                ))}
              </div>

              {isCurrent ? (
                <div style={{ ...S.btn("#9ca3af"), textAlign: "center", cursor: "default", display: "block", padding: "10px", borderRadius: 8 }}>
                  Current Plan
                </div>
              ) : (
                <button
                  style={{ ...S.btn(color), opacity: paying === plan.id ? 0.7 : 1 }}
                  onClick={() => handleUpgrade(plan.id)}
                  disabled={!!paying}
                >
                  {paying === plan.id ? "Processing…" : `Upgrade to ${plan.name}`}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* FAQ */}
      <div style={{ ...S.card, padding: 20, marginTop: 20 }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 14 }}>Frequently Asked Questions</div>
        {[
          ["Can I cancel anytime?",             "Yes, you can cancel your subscription anytime. You'll retain access until the end of your billing period."],
          ["Will I get a GST invoice?",          "Yes, a GST invoice is automatically generated for every payment and sent to your registered email."],
          ["What happens when trial ends?",      "After 14 days, your account is paused. You can upgrade anytime to restore full access. Your data is safe."],
          ["Can I upgrade or downgrade?",        "Yes, you can change plans anytime. Upgrades are immediate, downgrades apply at next billing cycle."],
          ["Is my payment secure?",              "Yes, all payments are processed by Razorpay — PCI DSS compliant and trusted by 8 million+ businesses."],
        ].map(([q, a]) => (
          <div key={q} style={{ marginBottom: 14, paddingBottom: 14, borderBottom: "1px solid #f3f4f6" }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{q}</div>
            <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.6 }}>{a}</div>
          </div>
        ))}
      </div>
    </div>

    {/* ── SUBSCRIPTION MANAGEMENT ── */}
    <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", overflow:"hidden", marginTop:16 }}>
      <div style={{ padding:"12px 16px", borderBottom:"1px solid #e5e7eb", fontWeight:700, fontSize:13 }}>📋 Subscription & Billing</div>
      <div style={{ padding:16 }}>
        <div style={{ background:"#f9fafb", borderRadius:8, padding:"12px 16px", marginBottom:16, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div>
            <div style={{ fontWeight:600, fontSize:13, marginBottom:2 }}>Current Plan: {(tenant?.plan||"trial").charAt(0).toUpperCase()+(tenant?.plan||"trial").slice(1)}</div>
            <div style={{ fontSize:12, color:"#6b7280" }}>
              {tenant?.plan==="trial"
                ? (tenant?.trialEndsAt ? `Trial ends ${new Date(tenant.trialEndsAt).toLocaleDateString("en-IN",{day:"2-digit",month:"long",year:"numeric"})}` : "Trial active")
                : "Active subscription · Auto-renews monthly"}
            </div>
          </div>
          {tenant?.plan!=="trial" && (
            <button onClick={async()=>{
              if (!window.confirm("Cancel? You keep access until end of billing period.")) return;
              try { await api("/billing/cancel",{method:"POST"}); toast("Cancelled. Access until end of period.","success"); }
              catch(e){ toast(e.message,"error"); }
            }} style={{ padding:"7px 16px", borderRadius:7, fontSize:12, cursor:"pointer", background:"#fef2f2", color:"#dc2626", border:"1px solid #fecaca", fontFamily:"inherit", fontWeight:600 }}>
              Cancel Subscription
            </button>
          )}
        </div>
        <div style={{ fontSize:11, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:10 }}>Payment History</div>
        {(tenant?.invoices?.length > 0) ? (
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
            <thead><tr>{["Date","Amount","Plan","Invoice","Status"].map(h=><th key={h} style={{ padding:"7px 10px", textAlign:"left", fontSize:10, fontWeight:700, color:"#9ca3af", background:"#f9fafb", borderBottom:"1px solid #e5e7eb" }}>{h}</th>)}</tr></thead>
            <tbody>
              {(tenant.invoices||[]).map((inv,i)=>(
                <tr key={i} style={{ borderBottom:"1px solid #f3f4f6" }}>
                  <td style={{ padding:"8px 10px" }}>{new Date(inv.date).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"})}</td>
                  <td style={{ padding:"8px 10px", fontWeight:600 }}>₹{inv.amount?.toLocaleString("en-IN")}</td>
                  <td style={{ padding:"8px 10px", textTransform:"capitalize" }}>{inv.plan}</td>
                  <td style={{ padding:"8px 10px" }}>{inv.invoiceUrl ? <a href={inv.invoiceUrl} target="_blank" rel="noreferrer" style={{ color:"#F47920", fontSize:11 }}>⬇ Download</a> : <span style={{ color:"#9ca3af" }}>—</span>}</td>
                  <td style={{ padding:"8px 10px" }}><span style={{ fontSize:10, padding:"2px 7px", borderRadius:8, background:"#f0fdf4", color:"#15803d", fontWeight:600 }}>Paid</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div style={{ padding:"20px 0", textAlign:"center", color:"#9ca3af", fontSize:12 }}>
            {tenant?.plan==="trial" ? "No payments yet — you're on the free trial" : "Payment history will appear here after your first invoice"}
          </div>
        )}
        <div style={{ marginTop:14, fontSize:12, color:"#9ca3af" }}>GST invoices emailed to <strong>{tenant?.email}</strong> after each payment. For help: <a href="mailto:info@3june.in" style={{ color:"#F47920" }}>info@3june.in</a></div>
      </div>
    </div>

    {/* ── REFERRAL ── */}
    <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", overflow:"hidden", marginTop:16 }}>
      <div style={{ padding:"12px 16px", borderBottom:"1px solid #e5e7eb", fontWeight:700, fontSize:13 }}>🎁 Refer a Seller, Get 1 Month Free</div>
      <div style={{ padding:16 }}>
        <div style={{ background:"linear-gradient(135deg,#1A3A5C,#2563eb)", borderRadius:8, padding:"16px 20px", marginBottom:16, color:"#fff" }}>
          <div style={{ fontSize:14, fontWeight:600, marginBottom:4 }}>Share your referral link</div>
          <div style={{ fontSize:12, opacity:0.8, marginBottom:12 }}>Every seller who signs up with your link earns you 1 free month of 3June.</div>
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            <input readOnly value={referral?.referralLink||"Loading…"}
              style={{ flex:1, padding:"8px 12px", borderRadius:6, border:"none", fontSize:12, background:"rgba(255,255,255,0.15)", color:"#fff", fontFamily:"monospace", outline:"none" }}
              onClick={e=>e.target.select()}/>
            <button onClick={copyLink}
              style={{ padding:"8px 16px", borderRadius:6, background:copying?"#15803d":"rgba(255,255,255,0.2)", color:"#fff", border:"none", cursor:"pointer", fontSize:12, fontWeight:600, fontFamily:"inherit", flexShrink:0 }}>
              {copying ? "✅ Copied!" : "📋 Copy"}
            </button>
          </div>
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:16 }}>
          {[
            { label:"Sellers referred",  value:referral?.referralCount||0,      color:"#2563eb" },
            { label:"Credits earned",    value:referral?.referralCredits||0,     color:"#15803d" },
            { label:"Credits available", value:referral?.creditsRemaining||0,   color:"#F47920" },
          ].map(k=>(
            <div key={k.label} style={{ background:"#f9fafb", borderRadius:8, padding:"12px 14px", border:"1px solid #e5e7eb" }}>
              <div style={{ fontSize:9, color:"#9ca3af", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:4 }}>{k.label}</div>
              <div style={{ fontSize:24, fontWeight:700, color:k.color }}>{k.value}</div>
            </div>
          ))}
        </div>

        {(referral?.creditsRemaining||0) > 0 && (
          <div style={{ background:"#f0fdf4", borderRadius:8, padding:"12px 16px", border:"1px solid #bbf7d0", display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
            <div>
              <div style={{ fontWeight:600, fontSize:13, color:"#15803d", marginBottom:2 }}>🎉 You have {referral.creditsRemaining} free month{referral.creditsRemaining>1?"s":""} to apply!</div>
              <div style={{ fontSize:12, color:"#6b7280" }}>Extends your trial or subscription by 30 days per credit</div>
            </div>
            <button onClick={applyCredit}
              style={{ padding:"8px 18px", borderRadius:7, background:"#15803d", color:"#fff", border:"none", cursor:"pointer", fontSize:13, fontWeight:600, fontFamily:"inherit" }}>
              Apply 1 Month
            </button>
          </div>
        )}

        {(referral?.referrals?.length||0) > 0 && (
          <div>
            <div style={{ fontSize:11, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:8 }}>Your Referrals</div>
            {referral.referrals.map((r,i)=>(
              <div key={i} style={{ display:"flex", justifyContent:"space-between", fontSize:12, padding:"6px 0", borderBottom:"1px solid #f3f4f6" }}>
                <span>{r.storeName}</span>
                <span style={{ color:"#9ca3af" }}>{new Date(r.joinedAt).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"})}</span>
              </div>
            ))}
          </div>
        )}

        <div style={{ marginTop:12, fontSize:12, color:"#9ca3af" }}>
          Your code: <strong style={{ fontFamily:"monospace", color:"#F47920" }}>{referral?.referralCode||"…"}</strong> ·
          Share via <a href={`https://wa.me/?text=I+use+3June+to+track+profit+on+Myntra+%26+Shopify.+Join+with+my+link:+${encodeURIComponent(referral?.referralLink||"")}`}
            target="_blank" rel="noreferrer" style={{ color:"#25D366", fontWeight:600 }}>WhatsApp</a>
        </div>
      </div>
    </div>
    </>
  );
}
